import type { Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { resolveOperationPresentationMode } from '@/workflow-runner/_pure/config/config';
import { derivePrimitiveOperationReplayEventKind } from '@/workflow-runner/_pure/primitiveOperationRod';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import type { Config } from '@/workflow-runner/_contract/config';
import type { ReplayEdgeEventDescriptor } from '@/workflow-runner/_contract/runtime';
import type { TraceSnapshot } from '@/workflow-runner/_contract/traceSnapshot';

export function classifyReplayEdgeEventForPositionMove(args: {
    fromSnapshot: TraceSnapshot | undefined;
    toSnapshot: TraceSnapshot | undefined;
    fromPosition: number;
    toPosition: number;
    config: Config;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
}): ReplayEdgeEventDescriptor {
    const { fromSnapshot, toSnapshot, fromPosition, toPosition, config } = args;
    if (Math.abs(toPosition - fromPosition) !== 1) return { eventKind: 'none' };

    // A snapshot owns the edge entering it, so the higher position owns the edge between the two.
    const edgeOwningSnapshot = toPosition > fromPosition ? toSnapshot : fromSnapshot;
    const structuralEventKind = edgeOwningSnapshot?.edgeEventKind ?? 'none';
    if (!fromSnapshot || !toSnapshot) return { eventKind: structuralEventKind };

    const toolStep = selectActiveToolStepFromTraceSnapshot(toSnapshot);
    const primitiveOperationEventKind = derivePrimitiveOperationReplayEventKind({
        direction: toPosition > fromPosition ? 'forward' : 'reverse',
        operationPresentationMode: resolveOperationPresentationMode(config),
        toolStep,
        preWorkflowState: fromSnapshot.position.workflowState,
        postWorkflowState: toSnapshot.position.workflowState,
        toolMap: args.toolMap,
    });
    if (primitiveOperationEventKind === 'primitive-operation') {
        return {
            eventKind: primitiveOperationEventKind,
            ...(structuralEventKind === 'none'
                ? {}
                : { continuationEventKind: structuralEventKind }),
        };
    }
    return {
        eventKind: structuralEventKind === 'none'
            && resolveOperationPresentationMode(config) === 'panels'
            && selectActiveToolStepFromTraceSnapshot(edgeOwningSnapshot) !== undefined
            ? 'tool-execution'
            : structuralEventKind,
    };
}
